/**
 * Phase 2b-3 — the in-workspace Artifact Studio editor surface: the OnlyOffice
 * editor (shirohige edit session) on the left, a Copilot sidecar on the right.
 *
 * The editor registers its OnlyOffice connector into OnlyOfficeConnectorContext
 * on document-ready, so the Copilot sidecar's write-to-document suggestions
 * (rendered by ChatThread → ToolPart → DocumentWriteCard) can be applied to the
 * live document on Accept. The sidecar also carries the AI-drafted badge and
 * the approval/export controls, so the "Copilot drafts, human approves"
 * boundary (vision §12) is enforced right beside the document.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { DocumentEditor } from '@onlyoffice/document-editor-react';
import { useMemo } from 'react';
import { OnlyOfficeConnectorProvider, useOnlyOfficeConnector } from '../../contexts/OnlyOfficeConnectorContext';
import { useAssemblePackage, useTransitionApproval } from '../../hooks/useArtifactStudio';
import { ActionGate } from '../../lib/access';
import { useEditSession } from '../../pages/Editor/hooks/useEditSession';
import { AiDraftedBadge } from './AiDraftedBadge';
import { ArtifactStudioCopilot } from './ArtifactStudioCopilot';
/** Wraps the editor body in the connector provider so the editor (which
 * registers the connector) and the Copilot sidecar (which applies edits
 * through it) share one OnlyOfficeConnectorContext. */ export function ArtifactStudioEditor(props) {
    return /*#__PURE__*/ _jsx(OnlyOfficeConnectorProvider, {
        children: /*#__PURE__*/ _jsx(ArtifactStudioEditorBody, {
            ...props
        })
    });
}
function ArtifactStudioEditorBody({ projectId, documentId, asset, onClose }) {
    const { data: session, isLoading, isError } = useEditSession(documentId);
    const transition = useTransitionApproval(projectId);
    const assemble = useAssemblePackage();
    const { setConnector } = useOnlyOfficeConnector();
    const config = useMemo(()=>{
        if (!session) return null;
        try {
            return JSON.parse(session.editorConfigJson);
        } catch  {
            return null;
        }
    }, [
        session
    ]);
    // objectType/objectId pin this resource to its scope node so the Phase 0.5
    // resolver walks the scope chain (tenant → project → asset) and honors an
    // admin's role assignment. Without them the resolver gets an empty scope
    // chain and fail-closes — the approve/export gate would never open. Must
    // match the target_object_type tesoro writes when it links the asset
    // (services/tesoro/.../artifact_studio.go → scopeTargetAsset).
    const resourceRef = {
        kind: 'artifact_studio_asset',
        id: asset.id,
        objectType: 'artifact_studio_asset',
        objectId: asset.id
    };
    const approved = asset.approvalState === 'approved';
    const editorId = `studio-editor-${documentId}`;
    const handleExport = ()=>{
        assemble.mutate({
            projectId,
            assetIds: [
                asset.id
            ],
            requiredArtifactTypes: asset.artifactType ? [
                asset.artifactType
            ] : []
        }, {
            onSuccess: (res)=>{
                window.alert(res.valid ? `Exported ${res.manifestStorageKeys.length} file(s).` : `Export blocked: ${res.blockedAssetIds.length} asset(s) not approved.`);
            }
        });
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex h-full min-h-0 flex-col",
        "data-testid": Testids.artifactStudio.editorSurface,
        children: [
            /*#__PURE__*/ _jsxs("header", {
                className: "flex items-center gap-3 border-b p-3",
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        variant: "outline",
                        size: "sm",
                        onClick: onClose,
                        children: "← Back"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "min-w-0 flex-1 truncate font-medium",
                        children: asset.title
                    }),
                    /*#__PURE__*/ _jsx(AiDraftedBadge, {
                        state: asset.approvalState,
                        generatedBy: asset.generatedBy
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex min-h-0 flex-1",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "min-w-0 flex-1",
                        children: [
                            isLoading && /*#__PURE__*/ _jsx("p", {
                                className: "p-4 text-sm text-muted-foreground",
                                children: "Opening editor…"
                            }),
                            isError && /*#__PURE__*/ _jsx("p", {
                                className: "p-4 text-sm text-destructive",
                                children: "Could not open the editor session."
                            }),
                            session && config && /*#__PURE__*/ _jsx(DocumentEditor, {
                                id: editorId,
                                documentServerUrl: session.documentServerUrl,
                                config: config,
                                events_onError: (ev)=>console.error('ONLYOFFICE error', ev),
                                events_onDocumentReady: ()=>{
                                    // Register the connector so DocumentWriteCard (in the Copilot
                                    // sidecar) can apply accepted suggestions to the live document.
                                    try {
                                        const editor = window.DocEditor?.instances?.[editorId];
                                        if (editor?.createConnector) {
                                            setConnector(editor.createConnector());
                                        }
                                    } catch  {
                                    // Connector creation is best-effort.
                                    }
                                }
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("aside", {
                        className: "flex w-96 shrink-0 flex-col border-l",
                        "data-testid": Testids.artifactStudio.copilotSidebar,
                        children: [
                            /*#__PURE__*/ _jsxs("section", {
                                className: "space-y-2 border-b p-3",
                                children: [
                                    /*#__PURE__*/ _jsx("h3", {
                                        className: "text-sm font-semibold",
                                        children: "Approval"
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex flex-wrap gap-2",
                                        children: [
                                            asset.approvalState === 'ai_drafted' && /*#__PURE__*/ _jsx(Button, {
                                                variant: "outline",
                                                size: "sm",
                                                disabled: transition.isPending,
                                                onClick: ()=>transition.mutate({
                                                        id: asset.id,
                                                        toState: 'in_review'
                                                    }),
                                                "data-testid": Testids.artifactStudio.submitReviewButton,
                                                children: "Submit for review"
                                            }),
                                            asset.approvalState === 'in_review' && /*#__PURE__*/ _jsx(ActionGate, {
                                                action: "approve",
                                                resource: resourceRef,
                                                children: /*#__PURE__*/ _jsx(Button, {
                                                    variant: "outline",
                                                    size: "sm",
                                                    onClick: ()=>transition.mutate({
                                                            id: asset.id,
                                                            toState: 'approved',
                                                            reason: 'approved in editor'
                                                        }),
                                                    "data-testid": Testids.artifactStudio.approveButton,
                                                    children: "Approve"
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(ActionGate, {
                                                action: "export",
                                                resource: resourceRef,
                                                alsoDisabled: !approved,
                                                children: /*#__PURE__*/ _jsx(Button, {
                                                    variant: "outline",
                                                    size: "sm",
                                                    onClick: handleExport,
                                                    "data-testid": Testids.artifactStudio.exportButton,
                                                    children: "Export"
                                                })
                                            })
                                        ]
                                    }),
                                    !approved && /*#__PURE__*/ _jsx("p", {
                                        className: "text-xs text-muted-foreground",
                                        children: "Export unlocks once the artifact is approved."
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "min-h-0 flex-1",
                                children: /*#__PURE__*/ _jsx(ArtifactStudioCopilot, {
                                    projectId: projectId,
                                    documentId: documentId,
                                    artifactType: asset.artifactType,
                                    title: asset.title
                                })
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
